import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, HowItWorks, BenefitsGrid, FAQSection, CTASection } from '../../components/PageTemplate';
import { 
  Search, FileText, CheckCircle, Home, Shield, 
  MessageCircle, Wrench, CreditCard, Users 
} from 'lucide-react';

export default function TenantsOverview() {
  const steps = [
    {
      title: 'Create Your Profile',
      description: 'Set up once with your details, employment info, and preferences. Reuse for every application.'
    },
    {
      title: 'Search Properties',
      description: 'Browse available properties with detailed photos, videos, and 3D tours.'
    },
    {
      title: 'Apply Instantly',
      description: 'One-click applications using your saved profile. No repeating the same information.'
    },
    {
      title: 'Complete Referencing',
      description: 'We verify your ID, check references, and confirm affordability - all online.'
    },
    {
      title: 'Sign & Pay',
      description: 'E-sign your tenancy agreement and pay your deposit securely online.'
    },
    {
      title: 'Move In',
      description: 'Complete check-in inventory via the app, collect keys, and you\'re home.'
    }
  ];

  const benefits = [
    {
      icon: FileText,
      title: 'Reusable Profile',
      description: 'Fill in your details once. Apply to multiple properties with one click.'
    },
    {
      icon: Shield,
      title: 'Transparent Process',
      description: 'See exactly where you are in the application process at every step.'
    },
    {
      icon: CheckCircle,
      title: 'Fast Referencing',
      description: 'Most references completed within 24-48 hours, not weeks.'
    },
    {
      icon: Home,
      title: 'Digital Everything',
      description: 'No printing, posting, or scanning. Sign contracts and pay online.'
    },
    {
      icon: MessageCircle,
      title: 'Direct Communication',
      description: 'Message your landlord or property manager directly through the app.'
    },
    {
      icon: Wrench,
      title: 'Easy Repairs',
      description: 'Report issues in-app with photos. Track progress until resolved.'
    },
    {
      icon: CreditCard,
      title: 'Simple Payments',
      description: 'Set up Direct Debit once. Rent collected automatically each month.'
    },
    {
      icon: Users,
      title: 'Fair Treatment',
      description: 'Clear processes, documented decisions, transparent deposit returns.'
    }
  ];

  const faqs = [
    {
      question: 'Is LetWise free for tenants?',
      answer: 'Yes. Creating a profile and applying to properties is completely free. You only pay your rent and deposit as normal.'
    },
    {
      question: 'What do I need to apply?',
      answer: 'Valid ID (passport or driving license), proof of income (payslips or accounts if self-employed), and your current landlord\'s contact details for a reference.'
    },
    {
      question: 'How long does referencing take?',
      answer: 'Most references are completed within 24-48 hours. It may take longer if your employer or previous landlord is slow to respond.'
    },
    {
      question: 'What happens to my deposit?',
      answer: 'Your deposit is protected with a government-approved scheme. At the end of your tenancy, it\'s returned minus any agreed deductions.'
    }
  ];

  const subPages = [
    { href: '/tenants/find-apply', title: 'Find & Apply', description: 'Search and apply', icon: Search },
    { href: '/tenants/referencing', title: 'Referencing', description: 'What we check', icon: Shield },
    { href: '/tenants/journey', title: 'Tenancy Journey', description: 'Your tenancy experience', icon: Home },
    { href: '/tenants/moving', title: 'Moving In/Out', description: 'Start and end', icon: CheckCircle },
    { href: '/tenants/faqs', title: 'FAQs', description: 'Questions answered', icon: MessageCircle },
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="FOR TENANTS"
        title="Finding a Home"
        subtitle="Should Be Simple"
        description="One profile. Instant applications. Transparent referencing. No more filling out the same forms over and over."
        primaryCTA={{ href: '/register', label: 'Create Free Profile' }}
        secondaryCTA={{ href: '/properties', label: 'Browse Properties' }}
        gradient="from-blue-50 via-white to-slate-50"
      />

      {/* Sub-page Navigation */}
      <section className="py-8 sm:py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {subPages.map((page) => (
              <Link
                key={page.href}
                to={page.href}
                className="group p-4 rounded-xl border hover:border-primary hover:bg-primary/5 transition-all text-center"
              >
                <page.icon className="w-6 h-6 mx-auto mb-2 text-slate-400 group-hover:text-primary transition-colors" />
                <div className="font-medium text-sm group-hover:text-primary transition-colors">{page.title}</div>
                <div className="text-xs text-slate-500 mt-1 hidden sm:block">{page.description}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks title="How It Works" steps={steps} />
      
      <BenefitsGrid title="Why Tenants Love LetWise" benefits={benefits} />

      <FAQSection faqs={faqs} />

      <CTASection
        title="Ready to find your new home?"
        description="Create your free profile and start applying in minutes."
        primaryCTA={{ href: '/register', label: 'Get Started Free' }}
        secondaryCTA={{ href: '/properties', label: 'Browse Properties' }}
      />
    </PublicLayout>
  );
}
